import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import Tip from "@/components/Tip";
import { MessageCircle, ArrowDownLeft, ArrowUpRight, RefreshCw, Wifi } from "lucide-react";

const POLL_MS = 5000;

export default function WhatsAppLogsPanel({ clinicId }) {
  const [logs, setLogs] = useState([]);
  const [polledAt, setPolledAt] = useState(null);
  const [live, setLive] = useState(true);
  const timer = useRef(null);

  const fetchLogs = async () => {
    try {
      const r = await api.get(`/clinics/${clinicId}/whatsapp-logs?limit=100`);
      setLogs(r.data.logs || []);
      setPolledAt(r.data.polled_at);
    } catch (e) { /* silent */ }
  };

  useEffect(() => {
    fetchLogs();
    if (live) timer.current = setInterval(fetchLogs, POLL_MS);
    return () => { if (timer.current) clearInterval(timer.current); };
  }, [clinicId, live]); // eslint-disable-line

  const fmt = (iso) => new Date(iso).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit", second: "2-digit" });

  return (
    <section className="solid-panel p-6 shadow-3d">
      <div className="flex items-end justify-between mb-4">
        <div>
          <h2 className="text-xl font-heading font-bold flex items-center">
            <MessageCircle className="h-5 w-5 mr-2 text-emerald-600" />
            WhatsApp Mirror UI
            <Tip text="Live feed of every WhatsApp message exchanged with your patients. Polls every 5 seconds. Direction arrows: ↘ inbound (patient) · ↗ outbound (clinic)." />
          </h2>
          <p className="text-sm text-slate-500 mt-1 flex items-center gap-2">
            <span className={`inline-block h-2 w-2 rounded-full ${live ? "bg-emerald-500 animate-pulse" : "bg-slate-400"}`} />
            {live ? "LIVE · polling every 5s" : "Paused"}
            {polledAt && <> · last polled {fmt(polledAt)}</>}
          </p>
        </div>
        <div className="flex gap-2">
          <button onClick={fetchLogs} className="text-sm text-[#004a99] underline" data-testid="wa-logs-refresh">
            <RefreshCw className="h-4 w-4 inline mr-1" /> Refresh now
          </button>
          <button onClick={() => setLive(!live)} className="text-sm text-slate-600 underline" data-testid="wa-logs-toggle-live">
            <Wifi className="h-4 w-4 inline mr-1" /> {live ? "Pause" : "Resume"}
          </button>
        </div>
      </div>

      {logs.length === 0 ? (
        <div className="text-center py-12 text-slate-400 text-sm" data-testid="wa-logs-empty">
          No WhatsApp messages yet. They will appear here in real time as patients message you.
        </div>
      ) : (
        <div className="space-y-2 max-h-[600px] overflow-y-auto pr-2">
          {logs.map((l) => {
            const inbound = l.direction === "inbound";
            return (
              <div key={l.id} data-testid={`wa-log-${l.id}`}
                   className={`flex gap-3 p-3 rounded-lg border ${inbound ? "bg-emerald-50 border-emerald-200" : "bg-blue-50 border-blue-200"}`}>
                <div className="flex-shrink-0">
                  {inbound
                    ? <ArrowDownLeft className="h-5 w-5 text-emerald-600 mt-0.5" />
                    : <ArrowUpRight className="h-5 w-5 text-blue-600 mt-0.5" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 text-xs">
                    <span className={`font-semibold ${inbound ? "text-emerald-700" : "text-blue-700"}`}>
                      {inbound ? "Patient" : "Clinic"} · {l.patient_name || l.from_number || l.to_number}
                    </span>
                    {l.purpose && <span className="px-1.5 py-0.5 rounded-full bg-white border text-[10px] uppercase tracking-wide text-slate-600">{l.purpose}</span>}
                    {l.simulated && <span className="text-amber-600 text-[10px]">simulated</span>}
                    <span className="ml-auto text-slate-500">{fmt(l.created_at)}</span>
                  </div>
                  <div className="mt-1 text-sm text-slate-800 whitespace-pre-wrap break-words">{l.body || (l.media_url ? "[media]" : "—")}</div>
                  {l.media_url && <a href={l.media_url} target="_blank" rel="noreferrer" className="text-xs text-[#004a99] underline">Media attachment</a>}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}
